import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StylesProvider } from "@insite/content-library/additionalStyles";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    accordionStyle?: InjectableCss;
    filterLogoImg?: InjectableCss;
    filterText?: TypographyProps;
    titleWrapper?: InjectableCss;
}

const styles: ProductListColumnsStyles = {
    container: {
        gap: 20,
    },
    leftColumnGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            border: none;
            & dl {
                flex-basis: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        null,
                        css`
                            flex-basis: 100%;
                        `,
                    ])}
            }
            & dd {
                padding: 0;
                border: none;
            }
            & dt button {
                border-bottom: none;
                &:focus {
                    border: 1px solid #cccccc;
                    border-bottom: none;
                }
                &[aria-expanded="false"] {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                border: 1px solid #cccccc;
                            `,
                            css`
                                border: 1px solid #cccccc;
                            `,
                            null,
                            null,
                            null,
                        ])}
                }
                svg {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            null,
                            null,
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                        ])}
                }
            }
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 9],
    },
    filterText: {
        css: css`    
        font-size: 32px;
        font-weight: 500; 
        color: #474747;
        ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(theme, [
            null,
            null,
            css`
                font-size: 23px;
            `,
            null,
            null,
        ])}
        `,
    },
    titleWrapper: {
        css: css`    
        width: 100%;
        display: flex;
        align-items: center;
        > div[class^="LazyImageStyle"] {
            width: 15%;
            margin-right: 10px;
        }
        `,
    },
};

export const columnsStyles = styles;

const ProductListColumns: FC<Props & ThemeProps<BaseTheme>> = ({ id, theme }) => {
    const [isAccordion, setAccordion] = React.useState(false);
    const [isAccordionExpanded, setAccordionExpanded] = React.useState(false);
    React.useEffect(() => {
        const setAccordionState = () => {
            if (window.innerWidth < theme.breakpoints.values[2]) {
                setAccordion(true);
            } else {
                setAccordion(false);
            }
        };
        setAccordionState();

        const onWindowResize = () => {
            setAccordionState();
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    });

    const onClickFilterSection = (_accordionExpanded: boolean) => () => {
        setAccordionExpanded(!_accordionExpanded);
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.leftColumnGridItem} data-test-selector="productListLeftCoulmn">
                <Accordion headingLevel={4} className="accordionFilterStyle">
                    <AccordionSection
                        title={
                            <StyledWrapper {...styles.titleWrapper}>
                                <LazyImage
                                    {...styles.filterLogoImg}
                                    src="/UserFiles/images/productlistingpage/filters.png"
                                ></LazyImage>
                                <Typography {...styles.filterText}>FILTERS</Typography>
                            </StyledWrapper>
                        }
                        {...(!isAccordion ? { expanded: true } : { expanded: isAccordionExpanded })}
                        onTogglePanel={onClickFilterSection(isAccordionExpanded)}
                    >
                        <Zone contentId={id} zoneName="Content00" />
                    </AccordionSection>
                </Accordion>
            </GridItem>
            <GridItem {...styles.rightColumnGridItem}>
                <Zone contentId={id} zoneName="Content01" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withTheme(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
